import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Container, Row, Table, Button, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faPlus, faEdit, faClock, faCalendarCheck, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import Loader from 'react-spinner-loader';
import './careerGuidanceListing.css';
import CreateCareerGuidanceSession from './createCareerGuidanceSession';
import CareerSessionSlots from './careerSessionSlots';
import { CareerGuidanceService } from '../../../../services/careerGuidanceService';
import SigniteButton from '../../../ui-elements/signite-button/signiteButton';
import { serverConfig } from '../../../../services/server-config';
import { CandidateService } from '../../../../services/candidateService';

const CareerGuidanceImage = ({ imageUrl, alt }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);

    useEffect(() => {
        let objectUrl = null;
        let cancelled = false;

        const fetchImage = async () => {
            if (!imageUrl) {
                setImageLoading(false);
                return;
            }

            if (imageUrl.startsWith("http")) {
                setImageSrc(imageUrl);
                setImageLoading(false);
                return;
            }
            try {
                setImageLoading(true);
                const url =
                    `https://${serverConfig.serverBaseAddress}/signite/file/download?path=${encodeURIComponent(imageUrl)}`;

                const response = await fetch(url, {
                    headers: CandidateService.getAuthHeader(),
                });

                if (!response.ok) {
                    throw new Error("Failed to load image");
                }

                const blob = await response.blob();

                if (cancelled) return;

                objectUrl = URL.createObjectURL(blob);

                setImageSrc(objectUrl);

            } catch (error) {
                console.error(
                    "Career Guidance image loading failed:",
                    error
                );
            } finally {
                if (!cancelled) {
                    setImageLoading(false);
                }
            }
        };

        fetchImage();

        return () => {
            cancelled = true;

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [imageUrl]);

    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f5f5f5"
            }}
        >
            {imageLoading && (
                <span style={{ fontSize: "12px", color: "#999" }}>
                    Loading...
                </span>
            )}
            {!imageLoading && imageSrc && (
                <img
                    src={imageSrc}
                    alt={alt || "Career Guidance"}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                    }}
                    decoding="async"
                />
            )}
        </div>
    );
};

const CareerGuidanceListing = () => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loader, setLoader] = useState(false);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(5);
    const [pageCount, setPageCount] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);
    const [showSlotModal, setShowSlotModal] = useState(false);
    const [selectedSessionForSlots, setSelectedSessionForSlots] = useState(null);
    const [activeTab, setActiveTab] = useState('sessions');
    const [bookings, setBookings] = useState([]);
    const [bookingLoader, setBookingLoader] = useState(false);

    const fetchBookings = async () => {
        setBookingLoader(true);
        try {
            const response =
                await CareerGuidanceService.getAllBookings(
                    pageNo,
                    pageSize
                );

            const data = response.data;
            setBookings(data.bookings || []);
            setPageCount(data.pageCount || 1);
            setTotalCount(data.totalCount || 0);
            setHasNext(data.hasNext || false);
        } catch (error) {
            console.error('Error fetching bookings:', error);
            setBookings([]);
            setTotalCount(0);
            setPageCount(1);
            setHasNext(false);
        } finally {
            setBookingLoader(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPageNo(0);
    };

    useEffect(() => {
        if (activeTab === 'sessions') {
            fetchSessions();
        } else if (activeTab === 'bookings') {
            fetchBookings();
        }

    }, [activeTab, pageNo, pageSize]);

    const fetchSessions = async () => {
        setLoader(true);
        try {
            const response =
                await CareerGuidanceService.getSessions(
                    pageNo,
                    pageSize
                );
            const data = response.data;
            setSessions(data.sessions || []);
            setPageCount(data.pageCount || 1);
            setTotalCount(data.totalCount || 0);
            setHasNext(data.hasNext || false);

        } catch (error) {
            console.error('Error fetching sessions:', error);
            setSessions([]);
            setTotalCount(0);
            setPageCount(1);
            setHasNext(false);

        } finally {
            setLoader(false);
        }
    };

    const getStatusClass = (status) => {
        switch ((status || '').toUpperCase()) {
            case 'ACTIVE':
                return 'status-success';

            case 'INACTIVE':
                return 'status-failed';

            default:
                return 'status-default';
        }
    };

    const getBookingStatusClass = (status) => {
        switch ((status || '').toUpperCase()) {
            case 'CONFIRMED':
                return 'status-success';

            case 'PENDING':
                return 'status-pending';

            case 'CANCELLED':
                return 'status-failed';

            default:
                return 'status-default';

        }
    };

    const handleCreate = () => {
        setSelectedSession(null);
        setIsEditMode(false);
        setShowCreateModal(true);

    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);

    };

    const handleCreateSession = async (formData) => {
        try {

            let imageUrl = formData.imageUrl;

            if (formData.image) {

                const uploadResponse =
                    await CareerGuidanceService.uploadSessionImage(
                        formData.image
                    );

                imageUrl = uploadResponse.data.path;
            }

            const request = {
                headline: formData.headline,
                descriptionText: formData.descriptionText,
                imageUrl: imageUrl,
                sessionType: formData.sessionType,
                bookingType: formData.bookingType,
                price: Number(formData.price),
                currency: formData.currency
            };

            if (isEditMode) {

                await CareerGuidanceService.updateSession(
                    formData.id,
                    {
                        descriptionText: request.descriptionText,
                        imageUrl: imageUrl,
                        sessionType: request.sessionType,
                        bookingType: request.bookingType,
                        price: request.price,
                        currency: request.currency
                    }
                );

            } else {
                await CareerGuidanceService.createSession(request);
            }
            setShowCreateModal(false);
            fetchSessions();

        } catch (error) {
            console.error(
                'Career Guidance session save failed:',
                error
            );
        }
    };

    const handleEdit = (session) => {
        setSelectedSession(session);
        setIsEditMode(true);
        setShowCreateModal(true);
    };

    const handleSlots = (session) => {
        setSelectedSessionForSlots(session);
        setShowSlotModal(true);
    };

    const handleCloseSlotModal = () => {
        setShowSlotModal(false);
        setSelectedSessionForSlots(null);

    };

    const renderPageNumbers = () => {
        const currentPage = pageNo + 1;
        const pageNumbers = [];

        if (pageCount <= 0) {
            return pageNumbers;
        }

        pageNumbers.push(1);

        if (pageCount > 5) {

            if (currentPage > 3) {
                pageNumbers.push('...');
            }

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(
                pageCount - 1,
                currentPage + 1
            );

            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }

            if (currentPage < pageCount - 2) {
                pageNumbers.push('...');
            }

            pageNumbers.push(pageCount);

        } else {

            for (let i = 2; i <= pageCount; i++) {
                pageNumbers.push(i);
            }
        }
        return pageNumbers;
    };

    const renderPagination = () => {
        if (pageCount <= 1) {
            return null;
        }
        return (
            <div className="pagination-controls mt-3">
                <Row className="align-items-center g-2 flex-nowrap">
                    <Col xs="auto">
                        <div className="pagination-container d-flex gap-1">
                            <button
                                className="border rounded p-1"
                                onClick={() => setPageNo(0)}
                                disabled={pageNo === 0}
                            >
                                {'<<'}
                            </button>
                            <button
                                className="border rounded p-1"
                                onClick={() =>
                                    setPageNo(
                                        Math.max(0, pageNo - 1)
                                    )
                                }
                                disabled={pageNo === 0}
                            >
                                {'<'}
                            </button>
                            {renderPageNumbers().map(
                                (page, index) => (

                                    <button
                                        key={index}
                                        className={`border rounded p-1 ${pageNo + 1 === page
                                            ? 'active'
                                            : ''
                                            }`}
                                        disabled={
                                            typeof page !== 'number'
                                        }
                                        onClick={() => {

                                            if (
                                                typeof page === 'number'
                                            ) {
                                                setPageNo(page - 1);
                                            }

                                        }}
                                    >
                                        {page}
                                    </button>

                                )
                            )}
                            <button
                                className="border rounded p-1"
                                onClick={() =>
                                    setPageNo(pageNo + 1)
                                }
                                disabled={!hasNext}
                            >
                                {'>'}
                            </button>
                            <button
                                className="border rounded p-1"
                                onClick={() =>
                                    setPageNo(pageCount - 1)
                                }
                                disabled={!hasNext}
                            >
                                {'>>'}
                            </button>
                        </div>

                    </Col>
                    <Col xs="auto">
                        <div className="d-flex align-items-center gap-2">
                            <span>Show</span>
                            <select
                                value={pageSize}
                                onChange={(e) => {

                                    setPageSize(
                                        Number(e.target.value)
                                    );

                                    setPageNo(0);

                                }}
                                className="border rounded p-1"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={30}>30</option>
                                <option value={40}>40</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    };

    const renderBookings = () => {
        return (
            <>
                <div className="total-batches-count">
                    Total Bookings : {totalCount}
                </div>

                <div className="table-responsive">
                    <Table
                        bordered
                        hover
                        className="career-guidance-table align-middle"
                    >
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Session</th>
                                <th>Candidate ID</th>
                                <th>Session Start Time</th>
                                <th>Session End Time</th>
                                <th>Price</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookingLoader ? (
                                <tr>
                                    <td
                                        colSpan="9"
                                        className="empty-message"
                                    >
                                        Loading Bookings...
                                    </td>

                                </tr>

                            ) : bookings.length === 0 ? (

                                <tr>
                                    <td
                                        colSpan="9"
                                        className="empty-message"
                                    >
                                        No Bookings Found.
                                    </td>
                                </tr>

                            ) : (

                                bookings.map((booking, index) => {
                                    const startDate = booking.startTime
                                        ? new Date(booking.startTime)
                                        : null;

                                    const endDate = booking.endTime
                                        ? new Date(booking.endTime)
                                        : null;

                                    const createdDate =
                                        new Date(booking.createdAt);

                                    return (
                                        <tr key={booking.id}>
                                            <td>
                                                {pageNo * pageSize + index + 1}
                                            </td>
                                            <td className="headline-column">
                                                <div className="headline-text">
                                                    {booking.sessionHeadline}
                                                </div>

                                            </td>
                                            <td>
                                                <a
                                                    href={`/candidate/candidate-personal-info?candidateId=${booking.candidateId}`}
                                                    style={{
                                                        color: '#f35824',
                                                        cursor: 'pointer',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {booking.candidateId}
                                                </a>
                                            </td>

                                            <td>
                                                {startDate
                                                    ? startDate.toLocaleString(
                                                        'en-IN',
                                                        {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        }
                                                    )
                                                    : 'Not Scheduled'}
                                            </td>
                                            <td>
                                                {endDate
                                                    ? endDate.toLocaleString(
                                                        'en-IN',
                                                        {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        }
                                                    )
                                                    : 'Not Scheduled'}
                                            </td>

                                            <td>
                                                {booking.currency}{' '}
                                                {booking.price}
                                            </td>

                                            <td>

                                                <span
                                                    className={`status - badge ${getBookingStatusClass(
                                                        booking.status
                                                    )
                                                        } `}
                                                >
                                                    {booking.status}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}

                        </tbody>
                    </Table>

                </div>
                <div className="pagination-container">
                    {renderPagination()}
                </div>
            </>
        );

    };


    return (

        <Container
            fluid
            className="career-guidance-container"
        >
            <Loader
                show={loader}
                type="body"
                stack="vertical"
                message="Loading Data"
            />

            <Row>
                <Col>
                    <Card className="career-guidance-card">
                        <Card.Header className="career-guidance-header">
                            <div className="career-guidance-title">
                                <div className="career-guidance-icon">
                                    <FontAwesomeIcon
                                        icon={faGraduationCap}
                                    />
                                </div>
                                <div>
                                    <h5>Career Guidance</h5>

                                    <small>
                                        Manage Career Guidance Sessions
                                    </small>
                                </div>
                            </div>
                            {activeTab === 'sessions' && (
                                <SigniteButton
                                    type="button"
                                    variant="primary"
                                    className="mb-2 ms-2"
                                    onClick={handleCreate}
                                >
                                    <FontAwesomeIcon
                                        icon={faPlusCircle}
                                        size="sm"
                                        className="me-2"
                                    />
                                    <span>Create Session</span>
                                </SigniteButton>
                            )}
                        </Card.Header>
                        <Card.Body>
                            <div className="career-guidance-tabs">
                                <button
                                    type="button"
                                    className={`career - guidance - tab ${activeTab === 'sessions'
                                        ? 'active'
                                        : ''
                                        } `}
                                    onClick={() =>
                                        handleTabChange('sessions')
                                    }
                                >

                                    <FontAwesomeIcon
                                        icon={faGraduationCap}
                                        className="me-2"
                                    />

                                    Sessions

                                </button>

                                <button
                                    type="button"
                                    className={`career - guidance - tab ${activeTab === 'bookings'
                                        ? 'active'
                                        : ''
                                        } `}
                                    onClick={() =>
                                        handleTabChange('bookings')
                                    }
                                >

                                    <FontAwesomeIcon
                                        icon={faCalendarCheck}
                                        className="me-2"
                                    />

                                    Bookings

                                </button>

                            </div>

                            {activeTab === 'sessions' && (

                                <>

                                    <div className="total-batches-count">
                                        Total Sessions : {totalCount}
                                    </div>

                                    <div className="table-responsive">

                                        <Table
                                            bordered
                                            hover
                                            className="career-guidance-table align-middle"
                                        >

                                            <thead>

                                                <tr>
                                                    <th>S.No</th>
                                                    <th>Image</th>
                                                    <th>Headline</th>
                                                    <th>Booking Type</th>
                                                    <th>Price</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>

                                            </thead>

                                            <tbody>

                                                {sessions.length === 0 ? (

                                                    <tr>

                                                        <td
                                                            colSpan="7"
                                                            className="empty-message"
                                                        >
                                                            No Career Guidance
                                                            Sessions Found.
                                                        </td>

                                                    </tr>

                                                ) : (

                                                    sessions.map(
                                                        (
                                                            session,
                                                            index
                                                        ) => (

                                                            <tr
                                                                key={
                                                                    session.id
                                                                }
                                                            >

                                                                <td>
                                                                    {pageNo *
                                                                        pageSize +
                                                                        index +
                                                                        1}
                                                                </td>

                                                                <td>
                                                                    <div
                                                                        style={{
                                                                            width: '70px',
                                                                            height: '70px',
                                                                            borderRadius: '8px',
                                                                            overflow: 'hidden'
                                                                        }}
                                                                    >
                                                                        {session.imageUrl ? (

                                                                            session.imageUrl.startsWith('http') ? (

                                                                                <Image
                                                                                    src={session.imageUrl}
                                                                                    rounded
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        objectFit: 'cover'
                                                                                    }}
                                                                                />

                                                                            ) : (

                                                                                <CareerGuidanceImage
                                                                                    imageUrl={session.imageUrl}
                                                                                    alt={session.headline}
                                                                                />

                                                                            )

                                                                        ) : (

                                                                            <div
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    background: '#f5f5f5',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    color: '#999',
                                                                                    fontSize: '12px'
                                                                                }}
                                                                            >
                                                                                No Image
                                                                            </div>

                                                                        )}
                                                                    </div>
                                                                </td>

                                                                <td className="headline-column">

                                                                    <div className="headline-text">
                                                                        {
                                                                            session.headline
                                                                        }
                                                                    </div>

                                                                    <small className="description-text">
                                                                        {
                                                                            session.descriptionText
                                                                        }
                                                                    </small>

                                                                </td>

                                                                <td>
                                                                    {session.bookingType === 'DIRECT_BOOKING'
                                                                        ? 'Direct Booking'
                                                                        : 'Slot Based'}
                                                                </td>

                                                                <td>
                                                                    {
                                                                        session.currency
                                                                    }{' '}
                                                                    {
                                                                        session.price
                                                                    }
                                                                </td>

                                                                <td>

                                                                    <span
                                                                        className={`status - badge ${getStatusClass(
                                                                            session.status
                                                                        )
                                                                            } `}
                                                                    >
                                                                        {
                                                                            session.status
                                                                        }
                                                                    </span>

                                                                </td>

                                                                <td>

                                                                    <div className="d-flex gap-2">
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline-primary"
                                                                            className="career-edit-btn"
                                                                            onClick={(e) => {
                                                                                handleEdit(session);
                                                                                e.currentTarget.blur();
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faEdit}
                                                                                className="me-1"
                                                                            />
                                                                            Edit
                                                                        </Button>

                                                                        {session.bookingType === 'SLOT_BASED' && (
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline-success"
                                                                                onClick={() => handleSlots(session)}
                                                                            >

                                                                                <FontAwesomeIcon
                                                                                    icon={faClock}
                                                                                    className="me-1"
                                                                                />

                                                                                Manage Slots

                                                                            </Button>
                                                                        )}

                                                                    </div>

                                                                </td>

                                                            </tr>

                                                        )
                                                    )

                                                )}

                                            </tbody>

                                        </Table>

                                    </div>

                                    <div className="pagination-container">
                                        {renderPagination()}
                                    </div>

                                </>

                            )}

                            {activeTab === 'bookings' && (

                                renderBookings()

                            )}

                        </Card.Body>

                    </Card>

                </Col>

            </Row>
            <CreateCareerGuidanceSession
                show={showCreateModal}
                handleClose={handleCloseCreateModal}
                handleSave={handleCreateSession}
                editMode={isEditMode}
                session={selectedSession}
            />
            <CareerSessionSlots
                show={showSlotModal}
                handleClose={handleCloseSlotModal}
                session={selectedSessionForSlots}
            />

        </Container>

    );

};

export default CareerGuidanceListing;
